import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, User, ArrowRight, Sparkles, Crown, ChefHat, Utensils, Loader2, Eye, EyeOff } from 'lucide-react';
import Logo from '@/components/Logo';
import { apiClient } from '@/services/api/apiClient';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';

interface DemoAccount {
  username: string;
  password: string;
  plan: string;
  plan_name: string;
  display_name: string;
  monthly_price: number;
  features: string[];
  colors: {
    gradient: string;
    bg: string;
    border: string;
    text: string;
  };
  icon: string;
}

const DemoLoginV3 = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [demoAccounts, setDemoAccounts] = useState<DemoAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState<DemoAccount | null>(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Fetch available demo accounts from backend
  useEffect(() => {
    const fetchDemoAccounts = async () => {
      try {
        const response = await apiClient.get('/demo/accounts/available');
        setDemoAccounts(response.data);

        // Check if there's a plan parameter in URL
        const planParam = searchParams.get('plan');
        if (planParam) {
          const account = response.data.find((acc: DemoAccount) =>
            acc.plan_name.toLowerCase() === planParam.toLowerCase()
          );
          if (account) {
            setSelectedAccount(account);
          }
        }
      } catch (err) {
        console.error('Failed to fetch demo accounts:', err);
        setError('Unable to load demo accounts. Please try again later.');
        setDemoAccounts([]);
      } finally {
        setAccountsLoading(false);
      }
    };

    fetchDemoAccounts();
  }, [searchParams]);

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'Crown':
        return Crown;
      case 'ChefHat':
        return ChefHat;
      case 'Utensils':
      default:
        return Utensils;
    }
  };

  const clearAllSessions = () => {
    // Clear all authentication-related storage
    localStorage.clear();
    sessionStorage.clear();

    // Clear all cookies
    document.cookie.split(";").forEach(function(c) {
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });

    console.log('All sessions and cookies cleared');
  };

  const handleSelectAccount = (account: DemoAccount) => {
    setSelectedAccount(account);
    setError('');
    setPassword('');
  };

  const handleDemoLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedAccount) {
      setError('Please select a demo account');
      return;
    }

    if (!password) {
      setError('Please enter the password');
      return;
    }

    setError('');
    setLoading(true);

    try {
      console.log(`Demo V3 login attempt for ${selectedAccount.plan} plan...`);

      // CRITICAL: Clear all existing sessions before logging in
      clearAllSessions();

      // Small delay to ensure cleanup completes
      await new Promise(resolve => setTimeout(resolve, 100));

      // Use V3 endpoint with proper cookie handling
      const response = await apiClient.post('/demo/v3/login', {
        username: selectedAccount.username,
        password: password
      }, {
        withCredentials: true  // Ensure cookies are sent/received
      });

      console.log('Demo V3 login response:', response.data);

      // No need to manually set cookies - backend handles it via httpOnly cookies
      // Just store demo flag in localStorage for UI purposes
      localStorage.setItem('isDemo', 'true');
      localStorage.setItem('demoPlan', selectedAccount.plan_name.toLowerCase());

      // Store restaurant info for UI
      if (response.data.restaurant_name) {
        localStorage.setItem('restaurant_name', response.data.restaurant_name);
      }
      if (response.data.rest_account_id) {
        localStorage.setItem('rest_account_id', response.data.rest_account_id.toString());
      }

      // Small delay to ensure cookies are properly set
      console.log('Login successful, navigating to dashboard...');
      setTimeout(() => {
        // Skip plan selection - go directly to dashboard
        navigate('/restaurant-dashboard');
      }, 100);
    } catch (err: any) {
      console.error('Demo V3 login error:', err);
      setError(err.response?.data?.detail || 'Invalid password');
    } finally {
      setLoading(false);
    }
  };

  const getRestaurantTitle = (planName: string) => {
    switch (planName.toLowerCase()) {
      case 'yummy':
        return 'Starter Restaurant';
      case 'delicious':
        return 'Growing Restaurant';
      case 'gourmet':
        return 'Premium Restaurant';
      default:
        return 'Demo Restaurant';
    }
  };

  return (
    <>
      <SEOHead
        title="Try Dine AI Demo | Experience Restaurant AI in Action"
        description="See how Dine AI transforms restaurants. Try our live demo with real data. No signup required. Experience the future of restaurant management."
        keywords="restaurant AI demo, try dine ai, restaurant software demo, AI restaurant demo"
      />
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] flex items-center justify-center p-4">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-4xl"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <Logo className="text-white h-12 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">
            {selectedAccount ? 'Enter Password to Continue' : 'Choose Your Demo Experience'}
          </h1>
          <p className="text-white/60">
            {selectedAccount
              ? `Logging in to ${getRestaurantTitle(selectedAccount.plan_name)}`
              : 'Select a plan to explore Dine AI\'s features'}
          </p>
        </div>

        {/* Loading State */}
        {accountsLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          </div>
        ) : selectedAccount ? (
          /* Password Input Form */
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md mx-auto"
          >
            <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-8">
              {/* Selected Account Info */}
              <div className={`inline-flex items-center gap-2 ${selectedAccount.colors.bg} ${selectedAccount.colors.border} border rounded-full px-4 py-2 mb-6`}>
                {(() => {
                  const Icon = getIconComponent(selectedAccount.icon);
                  return <Icon className={`w-5 h-5 ${selectedAccount.colors.text}`} />;
                })()}
                <span className={`${selectedAccount.colors.text} font-medium`}>
                  {selectedAccount.plan} Plan
                </span>
              </div>

              <h3 className="text-2xl font-bold text-white mb-2">
                {getRestaurantTitle(selectedAccount.plan_name)}
              </h3>
              <p className="text-white/60 mb-6">
                Username: <span className="text-white/90 font-mono">{selectedAccount.username}</span>
              </p>

              {/* Password Form */}
              <form onSubmit={handleDemoLogin} className="space-y-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                      className="w-full bg-white/5 border border-white/20 rounded-lg px-10 py-3 text-white placeholder-white/40 focus:outline-none focus:border-white/40 transition"
                      disabled={loading}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-500/20 border border-red-400/30 rounded-lg p-3"
                  >
                    <p className="text-red-300 text-sm">{error}</p>
                  </motion.div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-3 rounded-lg bg-gradient-to-r ${selectedAccount.colors.gradient} text-white font-medium transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Logging in...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <span>Login to Demo</span>
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  )}
                </button>
              </form>

              {/* Back Button */}
              <button
                onClick={() => {
                  setSelectedAccount(null);
                  setPassword('');
                  setError('');
                }}
                className="w-full mt-4 text-white/60 hover:text-white text-sm transition"
              >
                ← Back to plan selection
              </button>
            </div>
          </motion.div>
        ) : (
          /* Demo Account Cards */
          <div className="grid md:grid-cols-3 gap-6">
            {demoAccounts.map((account) => {
              const Icon = getIconComponent(account.icon);

              return (
                <motion.div
                  key={account.username}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="relative"
                >
                  <button
                    onClick={() => handleSelectAccount(account)}
                    className={`w-full bg-white/10 backdrop-blur-md rounded-2xl border ${account.colors.border} p-6 text-left transition-all hover:bg-white/15`}
                  >
                    {/* Plan Badge */}
                    <div className={`inline-flex items-center gap-2 ${account.colors.bg} ${account.colors.border} border rounded-full px-3 py-1 mb-4`}>
                      <Icon className={`w-4 h-4 ${account.colors.text}`} />
                      <span className={`${account.colors.text} text-sm font-medium`}>{account.plan} Plan</span>
                      {account.plan_name === 'gourmet' && (
                        <span className="text-xs">👑</span>
                      )}
                    </div>

                    {/* Plan Name */}
                    <h3 className="text-xl font-bold text-white mb-3">
                      {getRestaurantTitle(account.plan_name)}
                    </h3>

                    {/* Features */}
                    <ul className="space-y-2 mb-4">
                      {account.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className={`${account.colors.text} mt-0.5`}>✓</span>
                          <span className="text-white/70 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Action Button */}
                    <div className={`w-full py-2 rounded-lg bg-gradient-to-r ${account.colors.gradient} text-white font-medium text-center`}>
                      <span>Try {account.plan} Demo →</span>
                    </div>
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Info Text (only show when not on password screen) */}
        {!selectedAccount && (
          <div className="text-center mt-8">
            <p className="text-white/50 text-sm">
              💡 Each demo shows features specific to that subscription tier
            </p>
          </div>
        )}

        {/* Back to Regular Login */}
        <div className="text-center mt-8">
          <a
            href="/restaurant-login"
            className="text-white/60 hover:text-white text-sm transition flex items-center justify-center gap-2"
          >
            <span>← Back to regular login</span>
          </a>
        </div>
      </motion.div>
    </div>
    </>
  );
};

export default DemoLoginV3;
